import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { MessageSquare, Phone } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader } from "../ui";
import { usePlan } from "../../contexts/PlanContext";
import { hasPermission } from "../../utils/permissions";
import LeadCaptureBanner from "./LeadCaptureBanner";

/**
 * Enquiries workspace — the CRM shell that Enquiries and Calls share.
 *
 * They used to be two sidebar entries, which is wrong for how the work actually
 * happens: an enquiry *is* the reason for a call and a call *is* an event on an
 * enquiry, so a counsellor working a list moves between them constantly and had
 * to cross the whole sidebar each time.
 *
 * ── Why routes, not local state ──
 * Each tab is a real URL (`/enquiries`, `/enquiries/calls`). That keeps the back
 * button honest, makes a tab linkable, and — the reason that actually matters —
 * lets the missed-call notification the telephony worker writes keep pointing
 * somewhere real. `/calls` still resolves; App.js redirects it here.
 *
 * ── Two tab levels, deliberately different shapes ──
 * These page tabs are **underlined**; the sub-views inside Calls keep the app's
 * **pill** tabs. Two identical-looking rows stacked would read as one broken
 * control, and the counsellor would not know which row moved them where.
 *
 * ── The badge ──
 * "Needs callback" is the only number here that is a *task* rather than a
 * statistic, so it is the one thing worth surfacing on a tab the counsellor is
 * not currently looking at. It is fetched once here and handed down through the
 * outlet context, so opening the Calls tab does not re-request it.
 */

const TABS = [
  {
    to: "/enquiries",
    end: true,
    label: "Enquiries",
    icon: MessageSquare,
    title: "Enquiries",
    description: "Manage enquiries from prospective students",
    // This tab's content is an `AppDataTable` with `fill`, so the workspace
    // becomes a bounded column for it — see WORKSPACE below.
    fill: true,
  },
  {
    to: "/enquiries/calls",
    end: false,
    label: "Calls",
    icon: Phone,
    title: "Calls",
    description: "Every call made and received through your connected phone system",
    // Both gates, because either one alone leaves a tab that 404s on click.
    planFeature: "cloudTelephony",
    permission: "view_calls",
    // ⚠️ NO `fill`, and this is why the flag exists per tab rather than living
    // on the workspace. CallsPage still renders the legacy `Common/DataTable`,
    // which cannot shrink or scroll its own body — in a bounded column it would
    // simply overflow. It keeps today's behaviour until it moves to
    // `AppDataTable`.
  },
];

/*
 * Converted to utilities in Phase 5; enquiry-workspace.scss deleted in the same
 * commit (TOKEN_CONTRACT §6.1).
 *
 * The tabs are UNDERLINED, deliberately unlike the app's pill tabs
 * (`.custom-tabs-list`), because the Calls tab contains a pill row of its own.
 * Two identical controls stacked read as one broken widget; two different shapes
 * read as "page" and "view within page", which is what they are. Density is tuned
 * for the 15.6" @150% admin viewport — the rail is one 38px row.
 *
 * Two details that do not survive a careless conversion:
 *  - The breakpoint is `max-[576px]`, Bootstrap's `sm`, NOT Tailwind's `max-sm`
 *    (640px). Narrow viewports scroll rather than wrap, because a wrapped rail
 *    moves the content down a row and looks like two separate controls.
 *  - `scrollbar-width` is an arbitrary PROPERTY, so it takes no `tw-` prefix —
 *    `[scrollbar-width:none]` emits, `tw-[scrollbar-width:none]` emits nothing.
 */
// ⚠️ `tw-gap-4` replaces the `tw-mb-4` the rail used to carry. A margin on the
// rail and a gap on the column would be 32px, and the bounded column below has
// to own its own spacing — see the `gap` note on `.app-page-fill`.
const WORKSPACE = "tw-flex tw-flex-col tw-gap-4";

/*
 * The bounded page column, added only for a tab that asks for it (`TABS[].fill`).
 *
 * `app-page-fill` is the shell's ONE bounded-column class (styles/shell/
 * _content.scss): it owns the calc that subtracts the top nav, the support
 * banner and the content wrapper's padding. Written as a global class rather
 * than a `tw-h-[calc(…)]` here because those numbers are shell geometry — a
 * second copy in a component is exactly how the 56px topbar came to disagree
 * with the 64px one.
 *
 * It goes on the WORKSPACE, not on the Outlet child, because the tab rail is
 * inside this column: a child that bounded itself would have to subtract the
 * rail's height as a magic number, and the page would overflow by exactly that
 * much the day the rail changed.
 */
const WORKSPACE_FILL = "app-page-fill";

const TAB_RAIL =
  "tw-flex tw-items-stretch tw-gap-1 tw-border-b tw-border-line " +
  "max-[576px]:tw-overflow-x-auto max-[576px]:[scrollbar-width:none] " +
  "max-[576px]:[&::-webkit-scrollbar]:tw-hidden";

// The underline is drawn on the element itself rather than a pseudo-element, so
// it sits flush on the rail's own border instead of 1px above or below it — hence
// the transparent 2px bottom border and the -1px margin on every tab.
const TAB =
  "tw--mb-px tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap " +
  "tw-border-none tw-border-b-2 tw-border-b-transparent tw-bg-none tw-px-3.5 tw-pb-2.5 tw-pt-2 " +
  "tw-text-[0.8125rem] tw-font-medium tw-text-content-muted tw-no-underline tw-transition-colors " +
  "[&_svg]:tw-shrink-0 [&_svg]:tw-opacity-75 " +
  "focus-visible:tw-rounded-t focus-visible:tw-outline focus-visible:tw-outline-2 " +
  "focus-visible:-tw-outline-offset-2 focus-visible:tw-outline-primary";

const TAB_IDLE = "hover:tw-bg-surface-hover hover:tw-text-content";
const TAB_ACTIVE = "tw-border-b-primary tw-font-semibold tw-text-primary [&_svg]:tw-opacity-100";

// Callbacks waiting. The only number on this rail that is a task rather than a
// statistic, which is why it earns a place on a tab nobody is looking at.
const BADGE =
  "tw-inline-flex tw-h-[18px] tw-min-w-[18px] tw-items-center tw-justify-center tw-rounded-full " +
  "tw-bg-danger tw-px-[0.3rem] tw-text-[0.625rem] tw-font-bold tw-leading-none tw-text-on-status " +
  "[font-variant-numeric:tabular-nums]";

export default function EnquiryWorkspace() {
  const { pathname } = useLocation();
  const { hasPlanFeature } = usePlan();

  const [missed, setMissed] = useState([]);
  const [missedLoading, setMissedLoading] = useState(false);

  const tabs = useMemo(
    () => TABS.filter((t) => (
      (!t.planFeature || hasPlanFeature(t.planFeature))
      && (!t.permission || hasPermission(t.permission))
    )),
    [hasPlanFeature]
  );

  const canSeeCalls = tabs.some((t) => t.to === "/enquiries/calls");

  const reloadMissed = useCallback(() => {
    if (!canSeeCalls) return;
    setMissedLoading(true);
    apiClient.get("/telephony/calls/missed")
      .then(({ data }) => setMissed(data.calls || []))
      // A badge we couldn't load is not worth a toast — it silently reads zero,
      // and the Calls tab still shows the real list when opened.
      .catch(() => setMissed([]))
      .finally(() => setMissedLoading(false));
  }, [canSeeCalls]);

  useEffect(reloadMissed, [reloadMissed]);

  // Longest match wins, so `/enquiries/calls` doesn't also match the index tab.
  const active = [...tabs]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => (t.end ? pathname === t.to : pathname.startsWith(t.to))) || tabs[0];

  return (
    <div className={`${WORKSPACE}${active?.fill ? ` ${WORKSPACE_FILL}` : ""}`}>
      <PageHeader
        title={active?.title || "Enquiries"}
        description={active?.description}
        icon={active?.icon || MessageSquare}
      />

      <LeadCaptureBanner />

      {/* A single tab renders no rail at all — a tab strip you cannot move
          within is decoration, and on this viewport it costs a whole row. */}
      {tabs.length > 1 && (
        <nav className={TAB_RAIL} aria-label="Enquiries sections">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `${TAB} ${isActive ? TAB_ACTIVE : TAB_IDLE}`}
            >
              <tab.icon size={15} />
              <span>{tab.label}</span>
              {tab.to === "/enquiries/calls" && missed.length > 0 && (
                <span className={BADGE} title={`${missed.length} waiting for a callback`}>
                  {missed.length}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      )}

      <Outlet context={{ missed, missedLoading, reloadMissed }} />
    </div>
  );
}
